import React, {
  PureComponent,
  ReactNode,
  createRef,
  SyntheticEvent
} from 'react'
import { createPortal } from 'react-dom'
import { CloseIcon } from '../Icons'
import {
  ExitButton,
  Overlay,
  PopupContentWrapper,
  PopupModal
} from './Popup.elements'
import  { TransitionBox } from './TransitionBox'

type Props = {
  children: ReactNode
  closeIcon?: ReactNode
  maxWidth?: number
  onClose: () => void
  fadeOutOverlay?: boolean
  pushDown?: boolean
}

type State = {
  isOpen: boolean
}

export class Popup extends PureComponent<Props, State> {
  state: State = {
    isOpen: true
  }

  id: number = Math.floor(Math.random() * 1000)

  static defaultProps = {
    animate: 'transition'
  }

  popupRef = createRef<HTMLDivElement>()

  static openPopupsAmount: number = 0

  public static getAmountOfOpenedPopups() {
    return Popup.openPopupsAmount
  }

  componentDidMount() {
    document.addEventListener("keydown", this.handleKeyDown, false);
    document.body.style.overflow = 'hidden';
    Popup.openPopupsAmount++
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown, false);
    Popup.openPopupsAmount--
  }

  handleOverlayClick = (event: SyntheticEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) {
      this.close()
    }
  }

  handleFocus = () => {
    // Focus current popup modal for a better keyboard nav experience.
    this.popupRef.current && this.popupRef.current.focus({ preventScroll: true })
  }

  // Handy method to close the Popup whitout removing it drastically from DOM.
  //  This method allows to animate it off, then it calls `onClose` callback.
  public close = () => {
    this.setState({ isOpen: false })
    document.body.style.overflow = 'unset';
  }

  handleKeyDown = (event) => {
    if(event.key === 'Escape') {
      event.preventDefault();
      this.close()
    }
  }

  render () {
    const {
      children,
      maxWidth = 513,
      onClose,
      closeIcon,
      fadeOutOverlay = true,
      pushDown,
    } = this.props
    const { isOpen } = this.state

    const popup = (
      <Overlay
        key={this.id}
        onClick={this.handleOverlayClick}
        tabIndex={-1}
        leaving={!isOpen && fadeOutOverlay}
      >
        <TransitionBox
          isOpen={isOpen}
          onEntered={this.handleFocus}
          onExited={onClose}
          pushDown={pushDown}
        >
          <PopupModal
            maxWidth={maxWidth}
            tabIndex={0}
            ref={this.popupRef}
            pushDown={pushDown}
          >
            <PopupContentWrapper>
              <ExitButton
                onClick={this.close}
                aria-label="Close popup"
              >
                {closeIcon || <CloseIcon />}
              </ExitButton>
              {children}
            </PopupContentWrapper>
          </PopupModal>
        </TransitionBox>
      </Overlay>
    )

    return createPortal(popup, document.getElementsByTagName('main')[0] || document.body)
  }
}
